import { Popup } from '@syncfusion/ej2-popups';
import { addClass, removeClass, isNullOrUndefined as isNOU } from '@syncfusion/ej2-base';
import { getElementRect } from '../../../common/utils/dom';
/**
 * `Popup renderer` module is used to render popup in BlockEditor.
 *
 * @hidden
 */
export class PopupRenderer {
    constructor(manager) {
        this.parent = manager;
        this.editorElement = this.parent.rootEditorElement;
    }
    /**
     * Renders popup in BlockEditor.
     *
     * @param {IPopupRenderOptions} args - specifies  the arguments.
     * @returns {Popup} - returns the popup object.
     * @hidden
     */
    renderPopup(args) {
        let element = args.element;
        if (typeof element == 'string') {
            element = document.querySelector(element);
        }
        const popupObj = new Popup(element, {
            targetType: 'relative',
            relateTo: args.relateTo || this.editorElement,
            content: args.content,
            collision: { X: 'fit', Y: 'fit' },
            actionOnScroll: 'hide',
            showAnimation: {
                name: 'FadeIn'
            },
            width: args.width,
            height: args.height
        });
        popupObj.hide();
        return popupObj;
    }
    /**
     * Adjusts the popup position relative to the target element.
     *
     * @param {HTMLElement | Range} target - specifies the target element.
     * @param {Popup | Dialog} popup - specifies the popup object.
     * @returns {void}
     * @hidden
     */
    adjustPopupPositionRelativeToTarget(target, popup) {
        const isInlineTbar = popup.element.classList.contains('e-blockeditor-inline-toolbar-popup');
        const isblkActionPopup = popup.element.classList.contains('e-blockeditor-blockaction-popup');
        const isTableGripperPopup = popup.element.classList.contains('e-table-gripper-action-popup');
        const isImageUploadPopup = popup.element.classList.contains('e-image-upload-popup');
        if (isblkActionPopup) {
            this.positionBlockActionPopup(target, popup);
        }
        else if (isInlineTbar) {
            this.positionInlineToolbar(target, popup);
        }
        else if (isTableGripperPopup) {
            this.positionTableGripperActionPopup(target, popup);
        }
        else if (isImageUploadPopup) {
            this.positionImageUploadPopup(target, popup);
        }
    }
    positionBlockActionPopup(target, popup) {
        addClass([popup.element], 'e-be-action-popup-hide');
        const targetRect = target.getBoundingClientRect();
        const popupRect = popup.element.getBoundingClientRect();
        const editorRect = getElementRect(this.editorElement);
        const editorScrollTop = this.editorElement.scrollTop || 0;
        const adjustedX = targetRect.left - editorRect.left;
        let adjustedY = targetRect.top + editorScrollTop - editorRect.top + 30;
        // To position popup above the drag icon when it overflows out of editor.
        if (targetRect.bottom + popupRect.height > editorRect.bottom) {
            adjustedY = adjustedY - popupRect.height - targetRect.height - 15;
        }
        popup.position.X = adjustedX;
        popup.position.Y = adjustedY;
        removeClass([popup.element], 'e-be-action-popup-hide');
        popup.dataBind();
    }
    /**
     * Positions the image upload popup relative to the placeholder element.
     * The popup will appear below the placeholder if there's space, otherwise above.
     * Handles scroll offsets correctly.
     *
     * @param {HTMLElement} target - The placeholder element (target for positioning)
     * @param {Popup} popup - The image upload popup object
     * @returns {void}
     * @private
     */
    positionImageUploadPopup(target, popup) {
        // Hide popup temporarily while calculating position
        addClass([popup.element], 'e-image-popup-hide');
        // Get the block element (parent of placeholder)
        const blockElement = target.closest('.e-block');
        if (!blockElement || !target) {
            return;
        }
        // Get dimensions and positions
        const blockRect = blockElement.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const popupRect = popup.element.getBoundingClientRect();
        const editorRect = getElementRect(this.editorElement);
        const viewportHeight = window.innerHeight;
        // Calculate scroll offset
        const editorScrollTop = this.editorElement.scrollTop || 0;
        // Calculate relative X position (align with left edge of block)
        const adjustedX = targetRect.left - editorRect.left;
        // Calculate space available below and above the block
        const spaceBelow = viewportHeight - blockRect.bottom;
        const spaceAbove = blockRect.top;
        // Determine Y position based on available space
        let adjustedY;
        const popupOffset = 4; // Gap between block and popup
        if (spaceBelow >= popupRect.height || spaceBelow >= spaceAbove) {
            // Position below the block
            adjustedY = targetRect.bottom - editorRect.top + editorScrollTop + popupOffset;
        }
        else {
            // Position above the block
            adjustedY = targetRect.top - editorRect.top + editorScrollTop - popupRect.height - popupOffset;
        }
        // Set popup position
        popup.position.X = adjustedX;
        popup.position.Y = adjustedY;
        // Show popup and apply position
        removeClass([popup.element], 'e-image-popup-hide');
        popup.dataBind();
    }
    positionInlineToolbar(target, popup) {
        addClass([popup.element], 'e-be-inline-tlbr-hide');
        const popupRect = popup.element.getBoundingClientRect();
        const relativeElem = this.parent.currentFocusedBlock;
        if (isNOU(relativeElem)) {
            return;
        }
        const selection = this.parent.nodeSelection.getSelection();
        const clientRects = target.getClientRects();
        const direction = this.getSelectionDirection(selection);
        const rangeDomRect = clientRects.length === 0 ? target.getBoundingClientRect() :
            direction === 'Backward' ? clientRects[0] : clientRects[clientRects.length - 1];
        const offsetCalculationParam = {
            blockElement: relativeElem,
            blockRect: relativeElem.getBoundingClientRect(),
            range: target,
            rangeRect: rangeDomRect,
            direction: direction,
            contentPanelElement: this.parent.rootEditorElement,
            editPanelDomRect: this.parent.blockContainer.getBoundingClientRect(),
            popupRect: popupRect
        };
        const offsetX = this.calculateOffsetX(offsetCalculationParam);
        const offsetY = this.calculateOffsetY(offsetCalculationParam);
        let positionProps = {
            positionX: offsetX,
            positionY: offsetY
        };
        positionProps = this.handleVerticalCollision(offsetCalculationParam, positionProps);
        const popupProps = {
            offsetX: positionProps.positionX,
            offsetY: positionProps.positionY,
            relateTo: relativeElem
        };
        removeClass([popup.element], 'e-be-inline-tlbr-hide');
        popup.setProperties(popupProps);
        popup.dataBind();
    }
    // To calculate the popup offsetX position based on the range and block element position.
    calculateOffsetX(args) {
        const width = args.popupRect.width;
        let finalX;
        const buffer = 16.5; // A small gap between selection and popup
        const rangeEdge = args.direction === 'Backward' ? args.rangeRect.left : args.rangeRect.right;
        const relativePosition = rangeEdge - args.blockRect.left;
        if (relativePosition < width / 4) {
            finalX = relativePosition - buffer;
        }
        else if (relativePosition > width / 4 && relativePosition < width / 2) {
            finalX = relativePosition - width / 4;
        }
        else if (relativePosition > width / 2 && relativePosition < (width * 3 / 4)) {
            finalX = relativePosition - width / 2;
        }
        else if (relativePosition > (width * 3 / 4) && relativePosition < width) {
            finalX = relativePosition - (width * 3 / 4);
        }
        else {
            finalX = relativePosition - width + buffer;
        }
        return finalX;
    }
    // To calculate the popup offsetY position based on the range and block element position.
    calculateOffsetY(args) {
        const finalY = args.rangeRect.bottom - args.blockRect.top + 6;
        return finalY;
    }
    getSelectionDirection(selection) {
        if (selection && selection.rangeCount > 0 && selection.getRangeAt(0).collapsed) {
            return 'Forward';
        }
        const range = new Range();
        range.setStart(selection.anchorNode, selection.anchorOffset);
        range.setEnd(selection.focusNode, selection.focusOffset);
        if (range.collapsed) {
            return 'Backward';
        }
        else {
            return 'Forward';
        }
    }
    handleVerticalCollision(offsetParams, positionProps) {
        const scrollTopParentElement = this.parent.scrollParentElements && this.parent.scrollParentElements.length > 0 &&
            this.parent.scrollParentElements[0].nodeName !== '#document' ? this.parent.scrollParentElements[0] : this.parent.rootEditorElement;
        const scrollParentRect = scrollTopParentElement.getBoundingClientRect();
        const blockRect = offsetParams.blockRect;
        const topViewPortSpace = blockRect.top;
        const botViewPortSpace = blockRect.bottom;
        const spaceAbove = this.getSpaceAbove(offsetParams, scrollParentRect);
        const spaceBelow = this.getSpaceBelow(offsetParams, scrollParentRect);
        const totalPopupHeight = (10 + offsetParams.popupRect.height); // 10 si for tip pointer height
        const isTopPosition = this.isElemVisible(blockRect, 'top') && spaceAbove > totalPopupHeight && topViewPortSpace > totalPopupHeight;
        const isBotPosition = offsetParams.direction === 'Backward' && isTopPosition ? false : this.isElemVisible(blockRect, 'bottom') && spaceBelow > totalPopupHeight && botViewPortSpace > totalPopupHeight;
        if (isBotPosition) {
            return positionProps; // Default Bottom position no need to change offset.
        }
        else if (isTopPosition) {
            positionProps.positionY = -(offsetParams.popupRect.height + 10) + (offsetParams.rangeRect.top - offsetParams.blockRect.top);
        }
        return positionProps;
    }
    getSpaceAbove(args, scrollParentRect) {
        let spaceAbove;
        const blockRect = args.blockRect;
        const parentRect = args.editPanelDomRect;
        const collision = this.getTopCollisionType(blockRect, parentRect, scrollParentRect);
        switch (collision) {
            case 'ParentElement':
                spaceAbove = blockRect.top - parentRect.top;
                break;
            case 'ScrollableContainer':
                spaceAbove = scrollParentRect.top - parentRect.top;
                break;
            case 'ViewPort':
            case 'Hidden':
                spaceAbove = blockRect.top;
                break;
        }
        return spaceAbove;
    }
    getSpaceBelow(args, scrollParentRect) {
        let spaceBelow;
        const blockRect = args.blockRect;
        const parentRect = args.editPanelDomRect;
        const collision = this.getBottomCollisionType(blockRect, parentRect, scrollParentRect);
        switch (collision) {
            case 'Hidden':
            case 'ParentElement':
                spaceBelow = parentRect.bottom - blockRect.bottom;
                break;
            case 'ScrollableContainer':
                spaceBelow = scrollParentRect.bottom - blockRect.bottom;
                break;
            case 'ViewPort':
                spaceBelow = window.innerHeight - blockRect.bottom;
                break;
        }
        if ((window.innerHeight - blockRect.bottom) < (args.popupRect.height + 10)) { // check this 10 is tip pointer height
            spaceBelow = 0;
        }
        return spaceBelow;
    }
    getTopCollisionType(blockRect, parentRect, scrollParentRect) {
        if (blockRect.top < 0 || blockRect.top >= window.innerHeight) {
            return 'Hidden';
        }
        else {
            if (parentRect.top > 0) {
                return 'ParentElement';
            }
            else {
                if (scrollParentRect.top < 0) {
                    return 'ViewPort';
                }
                if (scrollParentRect.top > 0) {
                    return 'ScrollableContainer';
                }
            }
        }
        return 'ParentElement';
    }
    getBottomCollisionType(blockRect, parentRect, scrollParentRect) {
        if (blockRect.bottom < 0 || blockRect.bottom >= window.innerHeight) {
            return 'Hidden';
        }
        else {
            if (scrollParentRect.bottom >= window.innerHeight && parentRect.bottom >= window.innerHeight) {
                return 'ViewPort';
            }
            else {
                if (parentRect.bottom <= scrollParentRect.bottom) {
                    return 'ParentElement';
                }
                else {
                    return 'ScrollableContainer';
                }
            }
        }
    }
    // Returns true when the eleemnt is partially visible. Returns false when the element is not fully visible.
    isElemVisible(elemRect, value) {
        if (value === 'top') {
            return elemRect.top >= 0 && elemRect.top <= window.innerHeight;
        }
        else {
            return elemRect.bottom <= window.innerHeight && elemRect.bottom >= 0;
        }
    }
    positionTableGripperActionPopup(target, popup) {
        addClass([popup.element], 'e-be-gripper-action-popup-hide');
        const isColumnGripper = target.classList.contains('e-col-action-handle');
        const popupRect = popup.element.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const editorRect = this.editorElement.getBoundingClientRect();
        const scrollTop = this.editorElement.scrollTop;
        const offsetX = (targetRect.left - editorRect.left) + (isColumnGripper ? (targetRect.width / 2 - popupRect.width / 2) : 0);
        const offsetY = targetRect.top + scrollTop - editorRect.top - popupRect.height - 5;
        popup.position.X = offsetX;
        popup.position.Y = offsetY;
        removeClass([popup.element], 'e-be-gripper-action-popup-hide');
        popup.dataBind();
    }
    destroyPopup(popup) {
        if (popup) {
            popup.destroy();
            popup.element.remove();
        }
    }
}
